/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BR-16_CONFIRM_BRANDED_LINK
 * Date:         2026-06-23
 * Modified By:  @yair (Claude)
 *
 * Description:
 * Branded landing page for the inventory-confirmation email button.
 * The email now links here (PUBLIC_WEB_BASE_URL/inventory/confirm?token=...) instead of
 * directly to the API endpoint. Requires an explicit click so Outlook/Safe-Links scanners
 * that pre-fetch the URL cannot auto-confirm inventory. On click it calls the confirm API
 * (Accept: application/json) and routes to the existing confirm-result page.
 *
 * File Size Check:
 * - Current: 0 lines (new)
 * - Projected: ~70 lines
 * - Decision: Proceed (within 150-line component limit)
 *
 * Related Files:
 * - server/controllers/inventoriesController.js (confirmByToken — JSON mode)
 * - scheduler/src/jobs/inventory-confirmation/InventoryConfirmationJobHandler.js (confirmUrl)
 * - client/src/pages/InventoryConfirmResult.tsx (result page)
 */

import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Container, Card, Button, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxOpen } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { apiUrl } from "../config/environment";

const InventoryConfirm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const token = searchParams.get("token");

  const handleConfirm = async () => {
    if (!token) {
      navigate("/inventory/confirm-result?status=error&reason=invalid");
      return;
    }
    setBusy(true);
    try {
      await axios.get(
        `${apiUrl}/api/inventory/confirm/${encodeURIComponent(token)}`,
        { headers: { Accept: "application/json" } }
      );
      navigate("/inventory/confirm-result?status=success");
    } catch (err: any) {
      const reason = err?.response?.data?.reason || "invalid";
      navigate(`/inventory/confirm-result?status=error&reason=${reason}`);
    }
  };

  return (
    <Container className="py-5">
      <div className="d-flex justify-content-center">
        <Card style={{ maxWidth: "500px", width: "100%" }}>
          <Card.Body className="text-center py-5">
            <FontAwesomeIcon icon={faBoxOpen} size="4x" className="text-primary mb-4" />
            <h2 className="mb-3">Confirm your inventory</h2>
            <p className="text-muted mb-4">
              Confirm that your listed inventory is still current and available to buyers.
            </p>
            <Button
              variant="primary"
              size="lg"
              disabled={busy}
              onClick={handleConfirm}
              className="d-inline-flex align-items-center gap-2"
            >
              {busy ? (
                <>
                  <Spinner animation="border" size="sm" /> Confirming…
                </>
              ) : (
                "Confirm my inventory is current"
              )}
            </Button>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
};

export default InventoryConfirm;
